import { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Mail,
  Shield,
  ShieldCheck,
  ShieldAlert,
  UserPlus,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  ChevronLeft,
  ChevronRight,
  Save,
  X,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getUsers,
  addUser as addFirestoreUser,
  updateUser as updateFirestoreUser,
  deleteUser as deleteFirestoreUser,
  onUsersChange,
  type FirestoreUser
} from '@/services/firebase/adminFirestore';

type UserRole = 'admin' | 'moderator' | 'user';
type UserStatus = 'active' | 'inactive' | 'suspended';
type UserPlan = 'free' | 'pro' | 'enterprise';

export function AdminUsers() {
  const [users, setUsers] = useState<FirestoreUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<FirestoreUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('user');
  const [formPlan, setFormPlan] = useState<UserPlan>('free');
  const [formStatus, setFormStatus] = useState<UserStatus>('active');

  // Load users from Firebase with real-time updates
  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = onUsersChange((fetchedUsers) => {
      setUsers(fetchedUsers);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Calculate stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    paid: users.filter(u => u.plan === 'pro' || u.plan === 'enterprise').length,
    staff: users.filter(u => u.role === 'admin' || u.role === 'moderator').length,
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <ShieldCheck className="text-red-500" size={14} />;
      case 'moderator': return <Shield className="text-orange-500" size={14} />;
      default: return <Users className="text-text-tertiary" size={14} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">
          <CheckCircle2 size={10} /> Active
        </span>;
      case 'inactive':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">
          <Clock size={10} /> Inactive
        </span>;
      case 'suspended':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
          <XCircle size={10} /> Suspended
        </span>;
      default:
        return null;
    }
  };

  const getPlanBadge = (plan: string) => {
    const colors = {
      free: 'bg-gray-500/10 text-gray-500',
      pro: 'bg-accent/10 text-accent',
      enterprise: 'bg-purple-500/10 text-purple-500'
    };
    return (
      <span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', colors[plan as keyof typeof colors])}>
        {plan}
      </span>
    );
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === paginatedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(paginatedUsers.map(u => u.id));
    }
  };

  const handleAddUser = async () => {
    if (!formName || !formEmail) return;

    setIsSaving(true);
    try {
      await addFirestoreUser({
        name: formName,
        email: formEmail,
        role: formRole,
        status: formStatus,
        plan: formPlan,
        downloadsCount: 0,
        extensionsCount: 0,
      });
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to add user:', error);
      alert('Failed to add user. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setIsSaving(true);
    try {
      await updateFirestoreUser(editingUser.id, {
        name: formName,
        email: formEmail,
        role: formRole,
        status: formStatus,
        plan: formPlan,
      });
      resetForm();
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Failed to update user. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteFirestoreUser(id);
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleEditUser = (user: FirestoreUser) => {
    setFormName(user.name);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormStatus(user.status);
    setFormPlan(user.plan);
    setEditingUser(user);
  };

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormRole('user');
    setFormStatus('active');
    setFormPlan('free');
  };

  const handleBulkAction = async (action: 'activate' | 'suspend' | 'delete') => {
    if (selectedUsers.length === 0) return;

    if (action === 'delete') {
      if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
        for (const id of selectedUsers) {
          await deleteFirestoreUser(id);
        }
        setSelectedUsers([]);
      }
    } else {
      const status = action === 'activate' ? 'active' : 'suspended';
      for (const id of selectedUsers) {
        await updateFirestoreUser(id, { status });
      }
      setSelectedUsers([]);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.round((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      'day'
    );
  };

  const handleExport = () => {
    const csvContent = [
      ['ID', 'Name', 'Email', 'Role', 'Status', 'Plan', 'Created At'].join(','),
      ...users.map(u => [
        u.id,
        u.name,
        u.email,
        u.role,
        u.status,
        u.plan,
        u.createdAt.toISOString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
        <span className="ml-2 text-text-secondary">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Users</h1>
          <p className="text-text-secondary">Manage user accounts and permissions</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="btn btn-secondary">
            <Download size={16} />
            Export
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <UserPlus size={16} />
            Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
          <p className="text-sm text-text-secondary">Total Users</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-green-500">{stats.active}</p>
          <p className="text-sm text-text-secondary">Active Users</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-500">{stats.paid}</p>
          <p className="text-sm text-text-secondary">Paid Users</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-accent">{stats.staff}</p>
          <p className="text-sm text-text-secondary">Staff Members</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-bg-card border border-border-default rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
            <option value="user">User</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border-default">
            <span className="text-sm text-text-secondary">{selectedUsers.length} selected</span>
            <button
              onClick={() => handleBulkAction('activate')}
              className="btn btn-sm bg-green-500/10 text-green-500"
            >
              Activate
            </button>
            <button
              onClick={() => handleBulkAction('suspend')}
              className="btn btn-sm bg-yellow-500/10 text-yellow-500"
            >
              Suspend
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="btn btn-sm bg-red-500/10 text-red-500"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-bg-elevated border-b border-border-default">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === paginatedUsers.length && paginatedUsers.length > 0}
                  onChange={toggleAllUsers}
                  className="w-4 h-4 rounded border-border-default"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">User</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Role</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Plan</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Downloads</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Joined</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-text-tertiary">
                  No users found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-bg-elevated/50 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      className="w-4 h-4 rounded border-border-default"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          user.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{user.name}</p>
                        <p className="text-xs text-text-tertiary">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {getRoleIcon(user.role)}
                      <span className="text-sm text-text-primary capitalize">{user.role}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                  <td className="px-4 py-3">{getPlanBadge(user.plan)}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.downloadsCount || 0}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.createdAt.toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary">
                        <Mail size={16} />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border-default">
          <p className="text-sm text-text-tertiary">
            Showing {paginatedUsers.length} of {filteredUsers.length} users
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-bg-elevated hover:bg-bg-elevated/80 disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={cn(
                  'px-3 py-1 rounded-lg text-sm',
                  currentPage === page ? 'bg-accent text-white' : 'bg-bg-elevated hover:bg-bg-elevated/80'
                )}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-2 rounded-lg bg-bg-elevated hover:bg-bg-elevated/80 disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {(showAddModal || editingUser) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => { setShowAddModal(false); setEditingUser(null); resetForm(); }}
                className="p-1 hover:bg-bg-elevated rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as UserRole)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Plan</label>
                  <select
                    value={formPlan}
                    onChange={(e) => setFormPlan(e.target.value as UserPlan)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value as UserStatus)}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button
                onClick={() => { setShowAddModal(false); setEditingUser(null); resetForm(); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleAddUser}
                disabled={!formName || !formEmail || isSaving}
                className="btn btn-primary"
              >
                {isSaving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : editingUser ? (
                  <Save size={16} />
                ) : (
                  <UserPlus size={16} />
                )}
                {editingUser ? 'Save Changes' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
